import { createItem, deleteItem, getItem, listItems, updateItem } from './sharepoint';

const LIST = 'parts';

// Parts can ship to multiple plants, and within each plant they may be
// destined for specific vehicles. We store those links as
// PlantVehiclesJson: [{ plantCode: 'SHAP', vehicle: 'Ram 1500' }, ...]
// A part with no vehicle tagged for a plant simply means "all vehicles at
// that plant" — useful for shared parts like fasteners.
//
// Variants (color / option rows) are also stored inline as JSON to keep the
// list count low — a Ram Box with 11 colors is one Parts row, not 11.
function decode(row) {
  if (!row) return row;
  let variants = [];
  if (row.VariantsJson) {
    try {
      variants = JSON.parse(row.VariantsJson);
    } catch {
      variants = [];
    }
  }

  let plantVehicles = [];
  if (row.PlantVehiclesJson) {
    try {
      plantVehicles = JSON.parse(row.PlantVehiclesJson);
    } catch {
      plantVehicles = [];
    }
  } else if (row.PlantCodesJson) {
    // Back-compat: legacy rows stored only plant codes.
    try {
      plantVehicles = JSON.parse(row.PlantCodesJson).map((code) => ({
        plantCode: code,
        vehicle: '',
      }));
    } catch {
      plantVehicles = [];
    }
  } else if (row.PlantCode) {
    // Older still: single PlantCode text column.
    plantVehicles = [{ plantCode: row.PlantCode, vehicle: '' }];
  }

  // Convenience: unique plant codes this part goes to, for quick filtering /
  // chip displays. Callers shouldn't need to reach into plantVehicles for the
  // common "which plants?" question.
  const plantCodes = Array.from(
    new Set(plantVehicles.map((pv) => pv.plantCode).filter(Boolean)),
  );

  // Archive flag — a soft-delete. Archived parts stay in SharePoint (so
  // history/invoices that reference them still resolve) but are hidden from
  // the active Parts list by default and can be restored. SharePoint Yes/No
  // comes back as a JSON boolean; coerce defensively for legacy string rows.
  const archived =
    row.Archived === true || row.Archived === 'true' || row.Archived === 1;

  return { ...row, variants, plantVehicles, plantCodes, archived };
}

function encode(model) {
  const {
    variants = [],
    plantVehicles = [],
    // Drop the derived/legacy fields so they don't try to write back.
    plantCodes: _plantCodes,
    // `archived` is derived in decode(); it is intentionally NOT written
    // here so a normal Edit-form save can't accidentally un-archive (or
    // archive) a part. Archive state is changed only through
    // setPartArchived() below, which PATCHes just the Archived column.
    archived: _archived,
    ...rest
  } = model;
  return {
    ...rest,
    VariantsJson: JSON.stringify(variants),
    PlantVehiclesJson: JSON.stringify(plantVehicles),
    // Keep PlantCodesJson in sync for back-compat with any consumers still
    // reading the legacy column.
    PlantCodesJson: JSON.stringify(
      Array.from(new Set(plantVehicles.map((pv) => pv.plantCode).filter(Boolean))),
    ),
  };
}

export async function listParts(opts) {
  const rows = await listItems(LIST, { orderby: 'fields/Title', ...opts });
  return rows.map(decode);
}

/**
 * Find all parts a given plant receives. Plant membership is stored as a
 * JSON array inside PlantVehiclesJson, which Graph can't filter on natively —
 * so we fetch everything and filter in JS. Parts lists are small (dozens,
 * not thousands) so this is fine; we can add a denormalized search column
 * later if it ever becomes a hot path.
 */
export async function listPartsForPlant(plantCode) {
  const rows = await listParts();
  return rows.filter((p) => (p.plantCodes || []).includes(plantCode));
}

export async function listPartsForSupplier(supplierCode) {
  const rows = await listItems(LIST, {
    filter: `fields/SupplierCode eq '${supplierCode}'`,
    orderby: 'fields/Title',
  });
  return rows.map(decode);
}

export async function getPart(id) {
  return decode(await getItem(LIST, id));
}

/**
 * Find a part by its short code. Used by the detail page route.
 * Returns null if no match.
 */
export async function getPartByCode(partCode) {
  const rows = await listItems(LIST, {
    filter: `fields/PartCode eq '${partCode}'`,
    top: 1,
  });
  return rows.length ? decode(rows[0]) : null;
}

export async function createPart(model) {
  return decode(await createItem(LIST, encode(model)));
}

export async function updatePart(id, model) {
  return decode(await updateItem(LIST, id, encode(model)));
}

export async function deletePart(id) {
  return deleteItem(LIST, id);
}

/**
 * Archive (deactivate) or restore a part. This is a soft-delete: the row
 * stays in SharePoint so anything that references the part by code (history,
 * invoices, coverage) still resolves, but archived parts drop out of the
 * active Parts list by default and can be brought back.
 *
 * We PATCH only the `Archived` column so the rest of the part is untouched —
 * archive/restore never round-trips through encode(), which deliberately
 * omits Archived to protect it from normal Edit-form saves.
 *
 * NOTE: needs a Yes/No column named `Archived` on the Parts SharePoint list
 * (see provision-sharepoint-lists.mjs). Until that column exists, SharePoint
 * silently drops the field and the part won't actually archive.
 */
export async function setPartArchived(id, archived) {
  return decode(await updateItem(LIST, id, { Archived: !!archived }));
}

export async function archivePart(id) {
  return setPartArchived(id, true);
}

export async function restorePart(id) {
  return setPartArchived(id, false);
}
